import React, {
  ChangeEvent,
  ElementRef,
  forwardRef,
  KeyboardEvent,
  ReactNode,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import { useGlobalStore } from '../../state/globalStore';
import { KEY_CODES } from '../../utils/constants';
import { makeId } from '../../utils/helper';
import { StyledError } from './';

interface Props {
  label: ReactNode;
  error?: boolean;
  checked?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  validator?: (value: boolean) => undefined | string; // True if valid, Error string if invalid
}

export const Checkbox = forwardRef<{ validate: () => boolean; getValue: () => string }, Props>(
  ({ label, error = false, onChange, validator, ...props }: Props, ref) => {
    const checkboxRef = useRef<HTMLInputElement>(null);
    const id = useMemo(() => makeId(10), []);
    const [isFocused, setIsFocused] = useState(false);
    const [checkboxError, setCheckboxError] = useState<string>('');
    const styledErrorRef = useRef<ElementRef<typeof StyledError>>(null);
    const buttonColor =
      useGlobalStore((state) => state.leadCapture.primaryButtonStyle.buttonColor) ||
      'rgba(0, 173, 239)';

    const validate = (val: boolean | undefined) => {
      const _err = validator?.(val ?? false);
      if (!_err) {
        setCheckboxError('');
        return true;
      }
      setCheckboxError(_err);
      styledErrorRef.current?.shakeError();
      return false;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === KEY_CODES.ENTER) {
        checkboxRef.current?.click();
      }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      validate(e.target.checked);
    };

    useImperativeHandle(ref, () => ({
      validate: () => validate(checkboxRef.current?.checked),
      getValue: () => '',
    }));

    return (
      <CheckboxWrapper baseColor={buttonColor}>
        <CheckboxInputWrapper error={error} isFocused={isFocused} baseColor={buttonColor}>
          <label className="container">
            <CheckboxInput
              id={id}
              ref={checkboxRef}
              onKeyDown={onKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onChange={handleChange}
              {...props}
            />
            <span className="checkmark"></span>
          </label>
        </CheckboxInputWrapper>
        <label
          htmlFor={id}
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          {label}
        </label>
        {!!checkboxError && (
          <StyledError topOffset={28} ref={styledErrorRef} error={checkboxError} />
        )}
      </CheckboxWrapper>
    );
  },
);

const CheckboxWrapper = styled.div<{ baseColor: string }>`
  display: flex;
  position: relative;

  .container {
    display: block;
    position: relative;
    padding-left: 22px;
    margin-bottom: 22px;
    cursor: pointer;
    font-size: 22px;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  /* Hide the browser's default checkbox */
  .container input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }

  /* Checkbox */
  .checkmark {
    position: absolute;
    top: 0;
    left: 0;
    height: 22px;
    width: 22px;
    background-color: #eee;
    border-radius: 4px;
  }

  /* When the checkbox is checked, add a blue background */
  .container input:checked ~ .checkmark {
    background-color: ${({ baseColor }) => baseColor};
  }

  /* Create the checkmark/indicator (hidden when not checked) */
  .checkmark:after {
    content: '';
    position: absolute;
    display: none;
  }

  /* Show the checkmark when checked */
  .container input:checked ~ .checkmark:after {
    display: block;
  }

  /* Style the checkmark/indicator */
  .container .checkmark:after {
    left: 8px;
    top: 3.5px;
    width: 3.5px;
    height: 10px;
    border: solid white;
    border-width: 0 2.5px 2.5px 0;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
  }
`;

const CheckboxInputWrapper = styled.div<{ error: boolean; isFocused: boolean; baseColor: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1px;
  height: 24px;
  width: 24px;
  margin-right: 10px;
  border-radius: 6px;
  border: 2px solid transparent;
  ${({ error }) =>
    error &&
    css`
       {
        border: 2px solid #e22b12;
      }
    `}
  ${({ isFocused, baseColor }) =>
    isFocused &&
    css`
       {
        border: 2px solid ${baseColor};
      }
    `}
  &:hover {
    border: 2px solid ${({ baseColor }) => baseColor};
  }
`;

const CheckboxInput = styled.input.attrs({ type: 'checkbox' })`
  margin: 0;
`;
